import { Badge, Button } from '@/components'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAxios } from '@/context/axiosContext'
import { useEffect, useRef, useState } from 'react'
import { useAppSelector } from '@/redux/hooks'
import {
  BoardListItem,
  buttonName,
  CommentListItem,
  confirmMessages,
  getBadgeLabel,
  getHeaderTitle,
  getMobileBadgeClass,
  successMessages,
  TInput,
} from '../boardItem'
import useCopyToClipboard from '@/hooks/useCopyToClipboard'
import { InquiryDetailContents } from './inquiryDetailContents'
import { InquiryDetailBoardReply } from './inquiryDetailBoardReply'
import { useDialog } from '@/context/dialogContext'
import { InquiryUpdateBoard } from './inquiryUpdateBoard'
import { BoardHeader } from '../_components/boardHeader'
import { useResponsive } from '@/hooks/useResponsive'

interface Data {
  cmpnyCd: string
  nttSn: number
}

const InquiryDetailBoard: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const headerTitle = getHeaderTitle(window.location.pathname)
  const { showAlert, showConfirm } = useDialog()
  const [isEditMode, setIsEditMode] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [isCopied, copyToClipboard] = useCopyToClipboard()
  const { user } = useAppSelector((state) => state.auth)
  const commentRef = useRef<HTMLDivElement>(null)
  const [boardDetail, setBoardDetail] = useState<BoardListItem | null>(null)
  const [searchParams] = useSearchParams() // 쿼리 파라미터 사용
  const nttSn = Number(searchParams.get('nttSn')) // 게시판 번호
  const cmpnyCd = '001'
  const [height, setHeight] = useState('auto') // 상태로 높이를 관리
  const isGuest = boardDetail?.cstId === ''
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { isMobile, isTablet, isDesktop } = useResponsive()
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto' // 높이 초기화
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px` // 내용에 따라 높이 조정
    }
  }, [boardDetail?.replyCn])

  useEffect(() => {
    if (nttSn) {
      getBoardDetail() // nttSn 값이 있을 때 API 호출
    }
  }, [nttSn])

  const handleCopy = () => {
    const url = window.location.href // 현재 페이지 URL
    copyToClipboard(url)
  }

  const handleChange = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target

    setBoardDetail((prevState) => {
      if (!prevState) return null

      return {
        ...prevState,
        [name]: value,
      } as BoardListItem // 모든 필드를 포함하는 상태를 유지
    })
  }

  //detailView
  const getBoardDetail = async () => {
    try {
      const data: Data = {
        cmpnyCd: cmpnyCd,
        nttSn: nttSn,
      }

      if (!cmpnyCd || !nttSn) {
        return
      }

      const response = await axios.get(`/inquiry/detail`, {
        headers: { 'Cache-Control': 'no-cache' },
        params: { ...data },
      })

      const { boardDetail } = response.data.data
      setBoardDetail(boardDetail)
    } catch (err: any) {
      console.error('Error fetching boardDetail:', err)
    }
  }

  const handleModalAction = (type: 'update' | 'delete' | 'cancel') => {
    if (type === 'delete') {
      showConfirm({
        title: confirmMessages.delete,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          deleteBoard()
        },
      })
    } else if (type === 'cancel') {
      showConfirm({
        title: confirmMessages.cancel,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          setTimeout(() => {
            navigate(0) // 500ms 후에 페이지 이동
          }, 100)
        },
      })
    } else if (type === 'update') {
      showConfirm({
        title: confirmMessages.update,
        confirmBtnName: buttonName.confirm,
        cancelBtnName: buttonName.cancel,
        onConfirm() {
          setIsDisabled(true)
          setIsEditMode(true)
        },
      })
    }
  }

  const deleteBoard = async () => {
    try {
      const data: Data = {
        cmpnyCd: cmpnyCd,
        nttSn: nttSn,
      }

      if (!cmpnyCd || !nttSn) {
        return
      }

      await axios.post(`/inquiry/delete`, data, {
        headers: {
          'Content-Type': 'application/json', // JSON 형식으로 데이터를 보내도록 명시
        },
      })

      showAlert({
        type: 'success',
        title: successMessages.delete,
        btnName: buttonName.confirm,
        onClose() {
          setTimeout(() => {
            navigate(`/inquiry`)
          }, 100)
        },
      })
    } catch (err: any) {
      console.error('Error fetching deleteBoard:', err)
    }
  }

  const scrollToComment = () => {
    commentRef.current?.scrollIntoView({
      behavior: 'smooth', // 부드러운 스크롤
    })
  }

  return (
    <>
      {!isMobile && <BoardHeader title={headerTitle} />}
      {!isEditMode ? (
        <>
          <div
            style={{
              marginBottom: '10px',
              width: '100%',
              height: 'auto',
              padding: !isMobile ? '29px 29px 0' : undefined,
              borderRadius: '6px',
              border: '1px solid',
              borderColor: '#ebecef',
            }}
          >
            {!isMobile && (
              <Badge color="neverbe">
                {getBadgeLabel(boardDetail?.bbsSj || '기타')}
              </Badge>
            )}
            <InquiryDetailContents
              boardDetail={boardDetail}
              height={height}
              handleChange={handleChange}
              isGuest={isGuest}
              scrollToComment={scrollToComment}
              handleCopy={handleCopy}
              handleModalAction={handleModalAction}
              isMobile={isMobile}
            />
          </div>
          {!isMobile && (
            <InquiryDetailBoardReply
              boardDetail={boardDetail}
              textareaRef={textareaRef}
              isMobile={isMobile}
            />
          )}
        </>
      ) : (
        <InquiryUpdateBoard boardDetail={boardDetail} isMobile={isMobile} />
      )}
      {!isMobile && (
        <div ref={commentRef} style={{ display: 'flex', marginBottom: '10px' }}>
          <div style={{ marginLeft: 'auto' }}>
            {!isDisabled && (
              <div style={{ display: 'flex', gap: '5px' }}>
                {/* TODO 등록자에 2025.03.11 16시 이후로 cstId 값이 들어가도록 바꿔놔서 방어로직 추가했습니다. 
              코드 이대로 유지할지는 추후에 고민
            */}
                {(user?.userId === boardDetail?.regId ||
                  user?.cstId === boardDetail?.regId) && (
                  <>
                    <Button
                      color="neverbe"
                      onClick={() => handleModalAction('update')}
                    >
                      수정
                    </Button>
                    <Button
                      color="neverbe"
                      onClick={() => handleModalAction('delete')}
                    >
                      삭제
                    </Button>
                  </>
                )}
                <Button
                  color="neverbe"
                  onClick={() =>
                    navigate(`/inquiry`, { state: location.state })
                  }
                >
                  목록
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default InquiryDetailBoard
